import { ReactNode, useCallback, useState } from "react";

type BaseDialogState = {
  isOpen: boolean;
  message: string | ReactNode;
  mantraId: string;
  mantraTitle: string;
  [keyof: string]: any;
};

// common dialog state
export function useDialogState<T extends BaseDialogState>(initialState: T) {
  const [state, setState] = useState<T>(initialState);

  const open = useCallback((data: Omit<T, "isOpen">) => {
    setState({ ...data, isOpen: true } as T);
  }, []);

  const close = useCallback(() => {
    setState(initialState);
  }, [initialState]);

  return { state, open, close };
}

// Hook for Alert Dialog (delete confirm, activate/deactivate, etc.)
type AlertDialogState = BaseDialogState & {
  action: string;
};

export function useAlertDialog() {
  return useDialogState<AlertDialogState>({
    isOpen: false,
    message: "",
    mantraId: "",
    mantraTitle: "",
    action: "",
  });
}

// Hook for Reason Dialog (inactivate/reject/etc)
type ReasonDialogState = BaseDialogState;

export function useReasonDialog() {
  return useDialogState<ReasonDialogState>({
    isOpen: false,
    message: "",
    mantraId: "",
    mantraTitle: "",
  });
}

// Both alert + reason together
export function useConfirmationDialogs() {
  const alertDialog = useAlertDialog();
  const reasonDialog = useReasonDialog();
  return { alert: alertDialog, reason: reasonDialog };
}

// Legacy — kept for Mantra and Story pages only, do NOT use for new pages
export function useMantraDialog() {
  return useDialogState<any>({
    isOpen: false,
    mode: "create",
    mantraId: "",
  });
}

export function useStoryDialog() {
  return useDialogState<any>({
    isOpen: false,
    mode: "create",
    mantraId: "",
  });
}

// ─── Universal form dialog hook ───────────────────────────────────────────────
// Use this for ALL new create/edit dialogs going forward.
//
// Usage:
//   const dialog = useFormDialog({
//     mode: "create" as "create" | "edit",
//     adminId: "",
//     adminData: null as AdminType | null,
//   });
//   dialog.open({ mode: "edit", adminId: "123", adminData: {...} });
//   dialog.state.isOpen / dialog.state.mode / dialog.close()

type BaseExtras = {
  isOpen: boolean;
  message: string;
  mantraId: string;
  mantraTitle: string;
};

export function useFormDialog<T extends Record<string, unknown>>(
  initialState: T
) {
  const inner = useDialogState<T & BaseExtras>({
    ...initialState,
    isOpen: false,
    message: "",
    mantraId: "",
    mantraTitle: "",
  });

  // Plain wrapper — no extra hooks. Only pass your custom fields; base fields
  // (message, mantraId, mantraTitle) are merged in automatically.
  const open = (data: Partial<T>) => {
    inner.open({
      ...initialState,
      message: "",
      mantraId: "",
      mantraTitle: "",
      ...data,
    } as Omit<T & BaseExtras, "isOpen">);
  };

  return { state: inner.state, open, close: inner.close };
}

// ============================================
// USAGE EXAMPLE IN COMPONENTS
// ============================================

/*
// In UserManagement.tsx
const { alert, reason } = useConfirmationDialogs();

// Open alert dialog
alert.open({
  message: "Activate user?",
  userId: "123",
  userName: "John",
  action: "activate",
});

// Open reason dialog
reason.open({
  message: "Inactivate user?",
  userId: "123",
  userName: "John",
});

// Close dialogs
alert.close();
reason.close();

// Render dialogs
<AlertDialog isOpen={alert.state.isOpen} ... />
<ReasonDialog isOpen={reason.state.isOpen} ... />
*/
